/**
 * Single Journal post. Reads GET /api/blog/:slug (public, published only) and
 * renders the markdown body with a small, dependency-free renderer (the content
 * is admin-authored editorial markdown: headings, paragraphs, lists, links,
 * bold/italic). Luxury design.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';

interface Post {
  slug: string;
  title: string;
  excerpt: string | null;
  body_md: string | null;
  category: string | null;
  author: string | null;
  read_minutes: number | null;
  published_at: string | null;
}

// ── Minimal inline markdown (bold, italic, links) → React nodes ────────────
function inline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2]) nodes.push(<strong key={k++} style={{ color: 'var(--cream)' }}>{m[2]}</strong>);
    else if (m[3]) nodes.push(<em key={k++}>{m[3]}</em>);
    else if (m[4]) nodes.push(<a key={k++} href={m[5]} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--champagne-light)', textDecoration: 'underline' }}>{m[4]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

// ── Block-level markdown → React ───────────────────────────────────────────
function renderMarkdown(md: string): ReactNode[] {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let para: string[] = [];
  let list: string[] = [];
  let key = 0;

  const flushPara = () => {
    if (para.length) { out.push(<p key={key++} style={{ margin: '0 0 1.1rem', color: 'var(--cream-dim)', fontWeight: 300, lineHeight: 1.75 }}>{inline(para.join(' '))}</p>); para = []; }
  };
  const flushList = () => {
    if (list.length) { out.push(<ul key={key++} style={{ margin: '0 0 1.1rem', paddingLeft: '1.3rem' }}>{list.map((li, i) => <li key={i} style={{ color: 'var(--cream-dim)', fontWeight: 300, marginBottom: 6, lineHeight: 1.7 }}>{inline(li)}</li>)}</ul>); list = []; }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^###\s+/.test(line)) { flushPara(); flushList(); out.push(<h3 key={key++} style={{ color: 'var(--cream)', fontSize: '1.1rem', fontWeight: 600, margin: '1.6rem 0 0.6rem' }}>{inline(line.replace(/^###\s+/, ''))}</h3>); }
    else if (/^##\s+/.test(line)) { flushPara(); flushList(); out.push(<h2 key={key++} className="display" style={{ fontSize: '1.5rem', margin: '2rem 0 0.8rem' }}>{inline(line.replace(/^##\s+/, ''))}</h2>); }
    else if (/^[-*]\s+/.test(line)) { flushPara(); list.push(line.replace(/^[-*]\s+/, '')); }
    else if (line.trim() === '') { flushPara(); flushList(); }
    else para.push(line);
  }
  flushPara(); flushList();
  return out;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : iso + 'Z');
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ post: Post }>(`/api/blog/${slug}`)
      .then((d) => setPost(d.post))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="px-5 pt-[max(16px,env(safe-area-inset-top))]"><div className="card animate-pulse h-48" style={{ background: 'var(--surface)' }} /></div>;
  }
  if (!post) {
    return (
      <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] text-center py-20">
        <p className="font-semibold" style={{ color: 'var(--cream)' }}>Story not found</p>
        <button onClick={() => navigate('/community')} className="btn-outline mt-4">Back to Journal</button>
      </div>
    );
  }

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-10">
      <button onClick={() => navigate('/community')} className="p-1 mb-5" style={{ color: 'var(--champagne)' }} aria-label="Back">
        <ArrowLeft size={22} />
      </button>

      {post.category && (
        <p className="text-[11px] uppercase font-semibold mb-3" style={{ letterSpacing: '0.18em', color: 'var(--champagne)' }}>{post.category}</p>
      )}
      <h1 className="display mb-3" style={{ fontSize: 'clamp(1.9rem, 6vw, 2.6rem)', lineHeight: 1.1 }}>{post.title}</h1>
      <p className="text-xs mb-6" style={{ color: 'var(--cream-muted)' }}>
        {post.author ?? 'Regis Athletics'} · {fmtDate(post.published_at)}{post.read_minutes ? ` · ${post.read_minutes} min read` : ''}
      </p>

      <article style={{ maxWidth: 680 }}>{post.body_md ? renderMarkdown(post.body_md) : null}</article>
    </div>
  );
}
